// hooks/useTournamentRole.ts
/**
 * Hook to fetch and track the user's role for a specific tournament
 * This allows permissions to be tournament-aware
 */
import { useState, useEffect } from 'react';
import { ref, get, onValue } from 'firebase/database';
import { db } from '../config/firebase';
import type { UserData } from '../context/AuthContext';

export interface TournamentRoleResult {
  /** The user's role in the currently selected tournament */
  tournamentRole: string | null;
  /** Whether the role is being loaded */
  loading: boolean;
  /** The tournament ID the role is for */
  tournamentId: string | null;
  /** Function to manually set/change the tournament */
  setTournamentId: (id: string | null) => void;
  /** The effective role to use (tournament role if available, else default role) */
  effectiveRole: string;
}

/**
 * useTournamentRole - Hook for getting the user's role in a specific tournament
 *
 * When a tournament is selected, this fetches the user's per-tournament role
 * from tournaments/{id}/staffAssignments/{uid}/role
 *
 * Falls back to user.role (default role) if no tournament-specific role exists
 */
export function useTournamentRole(
  user: UserData | null,
  initialTournamentId?: string | null
): TournamentRoleResult {
  const [tournamentId, setTournamentId] = useState<string | null>(initialTournamentId || null);
  const [tournamentRole, setTournamentRole] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Sync internal state when the prop changes (e.g., when a tournament is selected)
  useEffect(() => {
    setTournamentId(initialTournamentId || null);
  }, [initialTournamentId]);

  // Default role from user object
  const defaultRole = user?.role || '';

  // Effective role: tournament-specific if available, else default
  const effectiveRole = tournamentRole || defaultRole;

  // Fetch tournament-specific role when tournament changes
  useEffect(() => {
    if (!user?.uid || !tournamentId) {
      setTournamentRole(null);
      setLoading(false);
      return;
    }

    setLoading(true);

    // Subscribe to real-time updates for the role
    const roleRef = ref(db, `tournaments/${tournamentId}/staffAssignments/${user.uid}/role`);

    const unsubscribe = onValue(roleRef, (snapshot) => {
      const role = snapshot.val();
      setTournamentRole(role || null);
      setLoading(false);
    }, (error) => {
      console.error('Error fetching tournament role:', error);
      setTournamentRole(null);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [user?.uid, tournamentId]);

  return {
    tournamentRole,
    loading,
    tournamentId,
    setTournamentId,
    effectiveRole,
  };
}

/**
 * Standalone function to fetch tournament role (non-reactive)
 * Useful for one-time checks
 */
export async function getTournamentRole(
  userId: string,
  tournamentId: string
): Promise<string | null> {
  try {
    const roleRef = ref(db, `tournaments/${tournamentId}/staffAssignments/${userId}/role`);
    const snapshot = await get(roleRef);
    return snapshot.val() || null;
  } catch (error) {
    console.error('Error fetching tournament role:', error);
    return null;
  }
}

export default useTournamentRole;
